import { useCallback, useMemo, useState } from 'react';
import { getClientDomain, getToken } from '../../../../utils/Common';

/**
 * ConfExceptions catalog (api-boxit-admin-usa, /v2/settings/exceptions).
 *
 * admin-usa owns the writes and pushes every change to the locker network through api-sync, so the
 * write responses carry a `sync` result: the row is already saved even when that push failed, and
 * the failure is recoverable with `syncAll` (POST /sync re-broadcasts the whole catalog).
 */
const EXCEPTIONS_URL = () => `${getClientDomain(2)}/settings/exceptions`;

const authHeaders = () => {
  const token = getToken?.() ?? sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const isAccessDenied = (status, json) => {
  if (status === 401 || status === 403) return true;
  const code = Number(json?.code);
  return code === 401 || code === 403;
};

/** `sync.code !== 200` means the row was saved but the lockers did not get it. */
const syncFailed = (json) => Boolean(json?.sync) && Number(json.sync.code) !== 200;

const normalize = (row) => ({
  exceptionId: row?.exceptionId ?? row?.IdException ?? null,
  code: row?.code ?? '',
  description: row?.description ?? '',
  engName: row?.engName ?? '',
  espName: row?.espName ?? '',
  porName: row?.porName ?? '',
  customerFacing: Boolean(row?.customerFacing),
  createdAt: row?.createdAt ?? null,
  status: Number(row?.status ?? 0),
});

export function useExceptions() {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    try {
      const res = await fetch(EXCEPTIONS_URL(), { method: 'GET', headers: authHeaders() });
      const json = await res.json().catch(() => null);

      if (isAccessDenied(res.status, json)) {
        setPermissionDenied(true);
        setExceptions([]);
        return;
      }

      if (json?.code !== 200 || !Array.isArray(json.payload)) {
        setExceptions([]);
        setError(json?.message || 'EXCEPTIONS_UNAVAILABLE');
        return;
      }

      setExceptions(json.payload.map(normalize));
    } catch (e) {
      setExceptions([]);
      setError(e.message || 'LOAD_ERROR');
    } finally {
      setLoading(false);
    }
  }, []);

  const createException = useCallback(
    async (draft) => {
      setSaving(true);
      try {
        const res = await fetch(EXCEPTIONS_URL(), {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            code: draft.code,
            description: draft.description,
            engName: draft.engName,
            espName: draft.espName,
            porName: draft.porName,
            customerFacing: Boolean(draft.customerFacing),
          }),
        });
        const json = await res.json().catch(() => ({}));

        if (json?.code !== 200) return { ok: false, error: json?.message || 'CREATE_FAILED' };

        await load();
        return { ok: true, exceptionId: json.exceptionId, syncFailed: syncFailed(json) };
      } catch (e) {
        return { ok: false, error: e.message || 'CREATE_FAILED' };
      } finally {
        setSaving(false);
      }
    },
    [load]
  );

  /** PATCH accepts partial fields: the form sends the whole record, the status switch sends `status`. */
  const updateException = useCallback(
    async (exceptionId, fields) => {
      setSaving(true);
      try {
        const res = await fetch(`${EXCEPTIONS_URL()}/${exceptionId}`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify(fields),
        });
        const json = await res.json().catch(() => ({}));

        if (json?.code !== 200) return { ok: false, error: json?.message || 'UPDATE_FAILED' };

        await load();
        return { ok: true, syncFailed: syncFailed(json) };
      } catch (e) {
        return { ok: false, error: e.message || 'UPDATE_FAILED' };
      } finally {
        setSaving(false);
      }
    },
    [load]
  );

  /** Inactivate / reactivate: IdStatus is the on/off switch the lockers read. */
  const setExceptionStatus = useCallback(
    (exceptionId, status) => updateException(exceptionId, { status: status ? 1 : 0 }),
    [updateException]
  );

  /** Recovery path: re-broadcasts the whole catalog to every locker. */
  // no platformIds = whole fleet (recovery broadcast); with platformIds = full catalog to those
  // lockers only (backend >= 2.108.0 / api-sync >= 1.38.0)
  const syncAll = useCallback(async (platformIds) => {
    setSyncing(true);
    try {
      const res = await fetch(`${EXCEPTIONS_URL()}/sync`, {
        method: 'POST',
        headers: authHeaders(),
        ...(platformIds?.length ? { body: JSON.stringify({ platformIds }) } : {}),
      });
      const json = await res.json().catch(() => ({}));

      if (json?.code !== 200) return { ok: false, error: json?.message || 'SYNC_FAILED' };

      return { ok: true, targets: json?.targets };
    } catch (e) {
      return { ok: false, error: e.message || 'SYNC_FAILED' };
    } finally {
      setSyncing(false);
    }
  }, []);

  const counts = useMemo(
    () => ({
      total: exceptions.length,
      active: exceptions.filter((e) => e.status === 1).length,
      inactive: exceptions.filter((e) => e.status !== 1).length,
    }),
    [exceptions]
  );

  return {
    exceptions,
    counts,
    loading,
    error,
    permissionDenied,
    saving,
    syncing,
    load,
    createException,
    updateException,
    setExceptionStatus,
    syncAll,
  };
}
